import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation, Translation } from 'react-i18next';
import ShipmentWebAPIUtils, {
  podEmailOnPodUpload,
  shipmentDocumentChanged,
} from 'utils/ShipmentWebAPIUtils';
import {
  carrierViewableDocTypesMap,
  DELIVERY_ORDER,
  INVOICE,
  PROOF_OF_DELIVERY,
  PAYOUT,
  shipperViewableDocTypesMap,
} from 'config/documents';
import {
  useCombineDocuments,
  useGetDocuments,
  useHandleDeleteForeignKeyRelationsDocuments,
  useHandleUploadDocuments,
  useProcessShipment,
} from 'utils/DocumentsHooks';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';
import {
  attachDocuments,
  RecognitionFieldTypes,
  waitDocumentUpload,
} from 'utils/DocumentsAPIUtils';
import { TablePagination } from '@cargomatic/ui';
import MultiSelectDocumentType from 'components/site/multi-document-type-select';
import { DocumentsUploader } from './DocumentsUploader';
import { DocumentsList } from './DocumentsList';
import { DOCUMENT_STATUSES } from './Document';
import Alert from 'components/ui/Alert';

const DEFAULT_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const shouldNotifyServerForMissingDocs = (shipment = {}) =>
  shipment.shipment_type === 'drayage' &&
  shipment.status === 'job_closed' &&
  shipment.shipper?._id &&
  shipment.driver?._id;

export function Documents({
  isOrder,
  carrierDocumentUpload,
  shipment,
  stop,
  showDropzone = true,
  deleteDisabled,
  onDelete,
  onDeleteFailed,
  onDeleteDecline,
  onUpload,
  onUploadFailed,
  refetchRef,
  shipments,
  isStop,
  stopShipmentId,
}) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [failedOrPendingDocuments, setFailedOrPendingDocuments] = useState([]);
  const [foreignKeys, setForeignKeys] = useState([shipment._id]);
  const [sentDocuments, setSentDocuments] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PER_PAGE,
  });
  const shipmentId = shipment?._id;
  const [filterDocumentTypes, setFilterDocumentTypes] = useState([]);
  const [refetchDocumentFilterType, setRefetchDocumentFilterType] =
    useState(false);

  useEffect(() => {
    const sentAudits = shipment?.audit?.filter(
      (audit) => audit.event === 'do_sent',
    );
    const sentDocumentsUri = sentAudits?.map((audit) => audit.data.uri);
    const sentDocuments = documents.filter((doc) =>
      sentDocumentsUri?.includes(doc.key),
    );
    setSentDocuments(sentDocuments);
  }, [shipmentId]);

  const userRole = useMemo(() => {
    const user = SessionStore.getUser();
    return user._type;
  }, []);

  const excludedDocTypes = [];

  const includedDocTypes = useMemo(() => {
    if (!userRole) {
      return [];
    }

    const userType = SessionStore.getUserType();
    let viewableDocsTypes = [];

    switch (userType) {
      case 'Shipper':
        viewableDocsTypes = Object.keys(shipperViewableDocTypesMap);
        break;
      case 'Carrier':
        viewableDocsTypes = Object.keys(carrierViewableDocTypesMap);
        break;
      default:
        viewableDocsTypes = [];
        break;
    }

    return viewableDocsTypes;
  }, []);

  const groupDocTypes = useMemo(() => {
    const userType = SessionStore.getUserType();
    const groupDocTypes = [];

    if (userType === 'Shipper' || isOrder) {
      groupDocTypes.push(INVOICE.value);
    }

    if (userType === 'Carrier' || isOrder) {
      groupDocTypes.push(PAYOUT.value);
    }

    if (['Carrier', 'Shipper'].includes(userRole)) {
      groupDocTypes.push(DELIVERY_ORDER.value);
    }

    return groupDocTypes;
  }, []);

  const {
    data: documentsData,
    error: documentError,
    refetch: documentsRefetch,
    loading,
  } = useGetDocuments({
    foreignKeys,
    type:
      filterDocumentTypes.length > 0 ? filterDocumentTypes : includedDocTypes,
    excludeTypes: excludedDocTypes,
    groupTypes: groupDocTypes,
    options: {
      limit: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
    fetchPolicy: 'network-only',
  });

  // Getting all documents without pagination for deciding whether to show Warning banner for Failed/ Pending Documents
  const { data: allDocumentsData, loading: allDocumentsLoading } =
    useGetDocuments({
      foreignKeys,
      type:
        filterDocumentTypes.length > 0 ? filterDocumentTypes : includedDocTypes,
      excludeTypes: excludedDocTypes,
      groupTypes: groupDocTypes,
      options: {
        limit: 1000,
        skip: 0,
        order: [{ field: 'createdAt', direction: 'desc' }],
      },
      fetchPolicy: 'network-only',
    });

  const showGetDocumentsErrorToast = (err) => {
    showToast(
      t('GET_DOCUMENT_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  const showDocumentDeletedToast = () => {
    showToast(t('DOCUMENT_DELETE_SUCCEED'), null);
  };

  const getPayoutIds = (shipment) => {
    return shipment.payables?.map((payable) => payable.payout_id) || [];
  };

  const getInvoiceIds = (shipment) => {
    return (
      shipment.receivables?.map((receivable) => receivable.invoice_id) || []
    );
  };

  useEffect(() => {
    if (isOrder) {
      return;
    }

    const keys = [
      shipment._id,
      ...getPayoutIds(shipment),
      ...getInvoiceIds(shipment),
    ];

    setForeignKeys([...new Set(keys)].filter((key) => key));
  }, [
    isOrder,
    shipmentId,
    shipment.payables?.length,
    shipment.receivables?.length,
  ]);

  useEffect(() => {
    if (!isOrder) {
      return;
    }

    const allShipmentKeys = shipments
      ?.map((shipment) => [
        ...getPayoutIds(shipment),
        ...getInvoiceIds(shipment),
      ])
      .flat();

    const keys = [shipment._id, ...allShipmentKeys];

    setForeignKeys([...new Set(keys)].filter((key) => key));
  }, [isOrder, shipments?.length]);

  const [processDocument] = useProcessShipment();
  const [combineDocuments] = useCombineDocuments();
  const refetchTimeoutRef = useRef(0);

  const refetchHandler = useCallback(() => {
    documentsRefetch();
  }, [documentsRefetch]);

  const { handleUpload } = useHandleUploadDocuments(
    shipment,
    stop,
    async (shipment, stop, type, uploadedData, userVisibility) => {
      if (type === PROOF_OF_DELIVERY.value) {
        let combinedDocument = await combineDocuments(
          uploadedData.map((document) => document.id),
          type,
          PROOF_OF_DELIVERY.name,
        );
        await waitDocumentUpload(combinedDocument.data.key);
        await attachDocuments(combinedDocument.data.id, [shipment.id]);
        await processDocument(combinedDocument.data.id, shipment.id, stop?.id, {
          recognitionFieldsType: RecognitionFieldTypes.GENERIC_UPLOAD_POD,
        });
        uploadedData = [combinedDocument.data];
        if (shipment.shipment_id)
          podEmailOnPodUpload(shipment, uploadedData[0]);
      }

      await documentsRefetch();
      setRefetchDocumentFilterType(true);

      if (onUpload) {
        return onUpload(shipment, stop, type, uploadedData, userVisibility);
      }
    },
    onUploadFailed,
  );

  const deleteFailed = async (documents, e) => {
    console.error(e);

    showToast(
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      'error',
    );

    return onDeleteFailed?.(documents, e);
  };

  const deleteDocuments = async (documentIds) => {
    const response = await ShipmentWebAPIUtils.deleteStopDocumentsV2(
      stopShipmentId,
      shipment,
      documentIds,
    ).catch((e) => {
      throw e;
    });
    return response;
  };

  const handleStopDocumentDelete = async (documents) => {
    try {
      setDeleting(true);
      const documentIds = documents.map((document) => document.id);
      await deleteDocuments(documentIds);
      showDocumentDeletedToast();
      documentsRefetch();
      setRefetchDocumentFilterType(true);
      return onDelete(documents);
    } catch (e) {
      return deleteFailed(documents, e);
    } finally {
      setDeleting(false);
    }
  };

  const { handleDelete } = useHandleDeleteForeignKeyRelationsDocuments(
    (documents) => {
      showDocumentDeletedToast();
      // update doc list after delete
      documentsRefetch();
      setRefetchDocumentFilterType(true);
      onDelete?.(documents);
    },
    onDeleteFailed,
  );

  const handleDocumentDelete = (documents) => {
    if (shouldNotifyServerForMissingDocs(shipment)) {
      shipmentDocumentChanged(shipment._id);
    }
    return isStop
      ? handleStopDocumentDelete(documents)
      : handleDelete(documents, true);
  };

  useEffect(() => {
    refetchRef?.(refetchHandler);

    return () => {
      refetchRef?.(refetchHandler);
    };
  }, [shipmentId, documentsRefetch]);

  useEffect(() => {
    if (documentError) {
      showGetDocumentsErrorToast(documentError);
    }
  }, [documentError]);

  useEffect(() => {
    documentsRefetch();
  }, [filterDocumentTypes]);

  useEffect(() => {
    const viewableDocuments = documentsData?.documents || [];

    setDocuments(viewableDocuments);

    const hasPendingDocuments = !!viewableDocuments.find(
      (document) => document.status === DOCUMENT_STATUSES.PENDING,
    );

    if (hasPendingDocuments) {
      clearTimeout(refetchTimeoutRef.current);

      refetchTimeoutRef.current = setTimeout(() => {
        refetchTimeoutRef.current = 0;
        refetchHandler();
      }, 10000);
    }
  }, [documentsData]);

  useEffect(() => {
    if (!allDocumentsLoading && allDocumentsData) {
      const allDocuments = allDocumentsData?.documents || [];

      const filteredDocuments = allDocuments.filter(
        (document) =>
          document.status === DOCUMENT_STATUSES.PENDING ||
          document.status === DOCUMENT_STATUSES.UPLOAD_FAILED,
      );
      setFailedOrPendingDocuments(filteredDocuments);
    }
  }, [allDocumentsData, allDocumentsLoading]);

  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (documentsData?.count != null) {
      setTotal(documentsData.count);
    }
  }, [documentsData]);
  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [filterDocumentTypes]);

  return (
    <div id="shipment-document-upload-zone" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectDocumentType
          shipmentId={shipment._id}
          foreignKeys={foreignKeys}
          filterDocumentTypes={filterDocumentTypes}
          setFilterDocumentTypes={setFilterDocumentTypes}
          excludedDocTypes={excludedDocTypes}
          includedDocTypes={includedDocTypes}
          refetch={refetchDocumentFilterType}
          setRefetch={setRefetchDocumentFilterType}
        />
      </div>
      {!allDocumentsLoading && failedOrPendingDocuments.length > 0 && (
        <Alert type="warning" className="w-full mb-4">
          {t('DOCUMENTS_IN_FAILED_PENDING_STATUS')}
        </Alert>
      )}
      <DocumentsList
        isOrder={isOrder}
        documents={documents}
        deleteDisabled={deleteDisabled}
        onDeleteConfirm={(document) => handleDocumentDelete([document])}
        onDeleteDecline={onDeleteDecline}
        sentDocuments={sentDocuments}
        loading={loading}
        pagination={pagination}
        setPagination={setPagination}
        pageCount={pageCount}
      />
      <TablePagination
        page={pagination.pageIndex + 1}
        pages={pageCount}
        total={total}
        perPage={pagination.pageSize}
        onPageChange={(p) =>
          setPagination((prev) => ({ ...prev, pageIndex: p - 1 }))
        }
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) =>
          setPagination(() => ({ pageIndex: 0, pageSize: size }))
        }
      />
      <hr />
      {showDropzone && (
        <DocumentsUploader
          parentType={isOrder ? 'order' : 'shipment'}
          shipment={shipment}
          carrierDocumentUpload={carrierDocumentUpload}
          onUpload={handleUpload}
        />
      )}
    </div>
  );
}
